import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { openDispatchSocket } from "@/lib/dispatchSocket";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusPill from "@/components/StatusPill";
import { GripVertical, Check, Truck, MapPin, Wand2, Scale, MessageSquare, Zap, ListOrdered, Sparkles, Download, Printer, BookMarked, Save, Pin, PinOff, ArrowUpDown, MoveVertical, X, Search, Camera, Radio, RotateCcw, Copy, UserPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { titleCase } from "@/lib/format";
import { toast } from "sonner";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

export default function Routing() {
    const [routes, setRoutes] = useState([]);
    const [activeDay, setActiveDay] = useState("monday");
    const [selectedRoute, setSelectedRoute] = useState(null);

    const load = async () => {
        const { data } = await api.get("/routes");
        setRoutes(data);
        setSelectedRoute((prev) => {
            // Preserve the user's current selection across reloads — grab the
            // fresh server copy of THAT route rather than snapping back to
            // "first route of the day". Fixes the bug where reordering a stop
            // felt like it didn't stick until a hard refresh.
            if (prev) {
                const fresh = data.find((r) => r.id === prev.id);
                if (fresh) return fresh;
            }
            return data.find((r) => r.day === activeDay) || data[0] || null;
        });
    };

    const refreshSelectedRoute = async () => {
        // Cheap targeted refresh — after a reorder/pin/optimize we only need
        // the current route's fresh state, not every route. Falls back to a
        // full load() when the single-route endpoint fails (e.g. deleted).
        if (!selectedRoute) return;
        try {
            const { data } = await api.get(`/routes/${selectedRoute.id}`);
            setSelectedRoute(data);
            setRoutes((rs) => rs.map((r) => (r.id === data.id ? data : r)));
        } catch {
            await load();
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    // Guard: any user-initiated mutation (drag reorder, pin toggle, master
    // save, etc.) grabs this ref while the round-trip is in flight. While
    // it's set, both the WS delta handler and the 20s poll skip refreshing
    // `selectedRoute` — otherwise a mid-flight PATCH races the poll and the
    // drag appears to snap back until the next reload.
    const mutationInFlight = useRef(false);

    // Live progress: subscribe to the dispatch WebSocket for stop-status
    // updates AND poll every 20s as a fallback in case WS drops. Both
    // paths just patch the in-memory routes state — no re-render churn on
    // the whole page.
    useEffect(() => {
        let cancelled = false;
        const applyDelta = (msg) => {
            if (!msg || msg.type !== "route.stop_updated") return;
            const patchStops = (stops) => (stops || []).map((s) =>
                s.id === msg.stop_id ? { ...s, status: msg.stop_status } : s
            );
            setRoutes((rs) => rs.map((r) => (
                r.id === msg.route_id ? { ...r, stops: patchStops(r.stops) } : r
            )));
            // Skip patching selectedRoute during an in-flight mutation so
            // the user's drag doesn't jitter back on a status-only delta.
            if (mutationInFlight.current) return;
            setSelectedRoute((cur) => (
                cur && cur.id === msg.route_id
                    ? { ...cur, stops: patchStops(cur.stops) }
                    : cur
            ));
        };
        const close = openDispatchSocket(applyDelta);
        const poll = setInterval(async () => {
            if (cancelled || mutationInFlight.current) return;
            try {
                const { data } = await api.get("/routes");
                if (cancelled || mutationInFlight.current) return;
                setRoutes(data);
                setSelectedRoute((cur) => {
                    if (!cur) return cur;
                    const fresh = data.find((r) => r.id === cur.id);
                    return fresh ? { ...cur, stops: fresh.stops } : cur;
                });
            } catch {
                // silent — poll is a fallback, WS is the fast path
            }
        }, 20000);
        return () => {
            cancelled = true;
            clearInterval(poll);
            close?.();
        };
    }, []);

    const dayRoutes = routes.filter((r) => r.day === activeDay);

    const moveStop = async (fromIdx, toIdx) => {
        if (!selectedRoute) return;
        if (fromIdx === toIdx) return;
        const stops = [...selectedRoute.stops];
        const [moved] = stops.splice(fromIdx, 1);
        stops.splice(toIdx, 0, moved);
        // Optimistic UI update so the drop feels instant
        const newRoute = { ...selectedRoute, stops };
        setSelectedRoute(newRoute);
        setRoutes(routes.map((r) => r.id === newRoute.id ? newRoute : r));
        mutationInFlight.current = true;
        try {
            await api.post(`/routes/${selectedRoute.id}/reorder`, {
                ordered_stop_ids: stops.map((s) => s.id),
            });
            // Then pull server truth so sequence numbers + any pin/anchor
            // side-effects are reflected without a page refresh.
            await refreshSelectedRoute();
        } catch (e) {
            toast.error("Failed to save order");
            // revert optimistic update on failure
            await refreshSelectedRoute();
        } finally {
            mutationInFlight.current = false;
        }
    };

    const markStopComplete = async (stopId) => {
        if (!selectedRoute) return;
        try {
            await api.patch(`/routes/${selectedRoute.id}/stops/${stopId}`, { status: "completed" });
            const updated = {
                ...selectedRoute,
                stops: selectedRoute.stops.map((s) =>
                    s.id === stopId ? { ...s, status: "completed" } : s
                ),
            };
            setSelectedRoute(updated);
            setRoutes(routes.map((r) => r.id === updated.id ? updated : r));
        } catch { toast.error("Failed"); }
    };

    const [optimizing, setOptimizing] = useState(false);
    const globalOptimize = async (highAccuracy = false) => {
        if (!selectedRoute?.stops?.length) return;
        const n = selectedRoute.stops.length;
        const label = highAccuracy ? "High accuracy" : "Fast";
        if (n > 40 && !window.confirm(
            `${label} global TSP for ${n} stops.\n\n` +
            (highAccuracy
                ? `Refines with real Google drive times after solving (~$0.25 in API cost, ~30-60 sec total).`
                : `Uses free straight-line drive-time estimates (~10 sec, no API cost). Best for weekly re-runs.`) +
            `\n\nContinue?`
        )) return;
        setOptimizing(true);
        toast.info(`${label} TSP running on ${n} stops…`);
        try {
            const { data } = await api.post(`/routes/${selectedRoute.id}/optimize-global`,
                { high_accuracy: highAccuracy });
            if (data.strategy === "noop") {
                toast.error(`Skipped — ${data.reason || "no result"}`);
            } else {
                const min = data.total_seconds ? Math.round(data.total_seconds / 60) : null;
                const meta = min ? ` — ${min} min drive` : "";
                const acc = data.high_accuracy ? " (real drive times)" : " (estimated)";
                toast.success(`Optimized ${data.ordered_stops.length} stops${meta}${acc}`);
                if ((data.unresolved || []).length > 0) {
                    toast.info(`${data.unresolved.length} stops couldn't be geocoded — kept at the end`);
                }
                await load();
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Global optimize failed");
        } finally { setOptimizing(false); }
    };

    const [seqOpen, setSeqOpen] = useState(false);
    const [seqData, setSeqData] = useState(null);
    const [selectedStopIds, setSelectedStopIds] = useState(new Set());
    const [templates, setTemplates] = useState([]);
    const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
    const [stopSearch, setStopSearch] = useState("");
    const [photoLightbox, setPhotoLightbox] = useState(null); // { url, stop }
    const [publishing, setPublishing] = useState(false);

    const publishRoute = async () => {
        if (!selectedRoute?.stops?.length) return;
        const label = `${selectedRoute.name} (${selectedRoute.stops.length} stops)`;
        if (!window.confirm(
            `Publish "${label}"?\n\nDrivers who have the app open will auto-refresh with this ` +
            `new stop order. Use this after you finish reorganizing.`
        )) return;
        setPublishing(true);
        try {
            const { data } = await api.post(`/routes/${selectedRoute.id}/publish`);
            toast.success(`Published — ${data.stop_count} stops. Drivers will refresh now.`,
                { duration: 5000 });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Publish failed");
        } finally { setPublishing(false); }
    };

    const stopSearchLower = stopSearch.trim().toLowerCase();
    const stopMatches = (s) => {
        if (!stopSearchLower) return true;
        return (s.customer_name || "").toLowerCase().includes(stopSearchLower)
            || (s.address || "").toLowerCase().includes(stopSearchLower);
    };
    const searchHitCount = selectedRoute?.stops
        ? selectedRoute.stops.filter(stopMatches).length : 0;

    const clearSelection = () => setSelectedStopIds(new Set());

    // Bulk "move to another truck's route" — opens a dialog where the
    // admin picks any OTHER route ON THE SAME DAY as the target (splitting
    // a Monday route between two Monday trucks is the primary use case).
    // Also supports creating a fresh same-day route on the fly for the
    // case where you decide to split mid-shift and no second truck route
    // exists yet.
    const [reassignOpen, setReassignOpen] = useState(false);
    const [reassignTargetId, setReassignTargetId] = useState("");
    const [reassignBusy, setReassignBusy] = useState(false);
    const [trucks, setTrucks] = useState([]);

    // --- Remove-duplicates flow ------------------------------------
    // Two-step: preview → confirm. Server returns groups; user un-checks
    // any duplicate they want to keep before hitting "Remove N stops".
    const [dedupeOpen, setDedupeOpen] = useState(false);
    const [dedupeData, setDedupeData] = useState(null);   // {groups:[…]}
    const [dedupeSelected, setDedupeSelected] = useState(new Set()); // stop_ids to remove
    const [dedupeBusy, setDedupeBusy] = useState(false);

    const openDedupeDialog = async () => {
        if (!selectedRoute) return;
        setDedupeBusy(true);
        setDedupeOpen(true);
        setDedupeData(null);
        try {
            const { data } = await api.get(`/routes/${selectedRoute.id}/duplicates`);
            setDedupeData(data);
            // Pre-select every duplicate stop by default (operator can
            // un-check any they want to keep before confirming).
            const all = new Set();
            (data.groups || []).forEach((g) =>
                g.duplicate_stop_ids.forEach((id) => all.add(id)));
            setDedupeSelected(all);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't scan for duplicates");
            setDedupeOpen(false);
        } finally {
            setDedupeBusy(false);
        }
    };

    const toggleDedupeStop = (stop_id) => {
        setDedupeSelected((prev) => {
            const next = new Set(prev);
            if (next.has(stop_id)) next.delete(stop_id);
            else next.add(stop_id);
            return next;
        });
    };

    const confirmDedupe = async () => {
        if (!selectedRoute || dedupeSelected.size === 0) return;
        setDedupeBusy(true);
        try {
            const { data } = await api.post(`/routes/${selectedRoute.id}/dedupe`, {
                stop_ids: [...dedupeSelected],
            });
            toast.success(`Removed ${data.removed} duplicate stop(s) — ${data.remaining_stops} remain`);
            setDedupeOpen(false);
            await load();
            await refreshSelectedRoute();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Dedupe failed");
        } finally {
            setDedupeBusy(false);
        }
    };
    // Inline "create new same-day route" fields — shown when the admin
    // toggles the "+ create new route" mode.
    const [creatingRoute, setCreatingRoute] = useState(false);
    const [newRouteName, setNewRouteName] = useState("");
    const [newRouteTruckId, setNewRouteTruckId] = useState("");
    const [newRouteTruckType, setNewRouteTruckType] = useState("sideload");

    // Populate the truck list lazily the first time the dialog opens.
    // Keeps the routing page snappy on initial load.
    const ensureTrucksLoaded = async () => {
        if (trucks.length > 0) return;
        try {
            const { data } = await api.get("/fleet/trucks");
            setTrucks(data.filter((t) => t.status === "active"));
        } catch { /* non-fatal — the dropdown will just be empty */ }
    };

    const openReassignDialog = () => {
        if (selectedStopIds.size === 0) {
            toast.info("Pick stops first (tap the checkbox on each).");
            return;
        }
        setReassignTargetId("");
        setCreatingRoute(false);
        setNewRouteName("");
        setNewRouteTruckId("");
        setReassignOpen(true);
        ensureTrucksLoaded();
    };

    // Only routes on the SAME DAY are valid split targets. Splitting a
    // Monday route to a Tuesday truck would mean those customers get
    // picked up on a different day — never what the operator wants.
    const sameDayRoutes = routes.filter(
        (r) => r.id !== selectedRoute?.id
              && r.day === selectedRoute?.day,
    );

    const confirmReassign = async () => {
        if (!selectedRoute) return;
        setReassignBusy(true);
        try {
            let targetId = reassignTargetId;
            // If the admin toggled "create new route", create it first and
            // use its id as the target.
            if (creatingRoute) {
                if (!newRouteName.trim() || !newRouteTruckId) {
                    toast.info("Give the new route a name and pick a truck.");
                    setReassignBusy(false);
                    return;
                }
                const { data: created } = await api.post("/routes", {
                    name: newRouteName.trim(),
                    day: selectedRoute.day,
                    truck_id: newRouteTruckId,
                    truck_type: newRouteTruckType,
                });
                targetId = created.id;
                toast.success(`Created "${created.name}" — routing the stops now…`);
            }
            if (!targetId) {
                toast.info("Pick a destination route or create a new one.");
                setReassignBusy(false);
                return;
            }
            const { data } = await api.post("/routes/stops/reassign", {
                source_route_id: selectedRoute.id,
                target_route_id: targetId,
                stop_ids: [...selectedStopIds],
            });
            toast.success(
                `Moved ${data.moved} stop(s) to "${data.target_route.name}" ` +
                `· source now ${data.source_route.remaining_stops}, ` +
                `target now ${data.target_route.total_stops}`
            );
            setReassignOpen(false);
            clearSelection();
            await load();
            await refreshSelectedRoute();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reassign failed");
        } finally {
            setReassignBusy(false);
        }
    };
    const toggleStopSelect = (id, shiftKey = false, allIds = []) => {
        setSelectedStopIds((prev) => {
            const next = new Set(prev);
            if (shiftKey && next.size > 0) {
                // Extend selection from the last-selected to id.
                const last = [...next].pop();
                const a = allIds.indexOf(last);
                const b = allIds.indexOf(id);
                if (a >= 0 && b >= 0) {
                    const [lo, hi] = [Math.min(a, b), Math.max(a, b)];
                    for (let i = lo; i <= hi; i++) next.add(allIds[i]);
                    return next;
                }
            }
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const persistOrder = async (stops) => {
        mutationInFlight.current = true;
        try {
            await api.post(`/routes/${selectedRoute.id}/reorder`, {
                ordered_stop_ids: stops.map((s) => s.id),
            });
            // Pull fresh server truth so seq numbers + pin anchors match
            await refreshSelectedRoute();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save reorder failed");
            await refreshSelectedRoute();
        } finally {
            mutationInFlight.current = false;
        }
    };

    const moveSelectedToPosition = async () => {
        if (selectedStopIds.size === 0) {
            toast.info("Pick stops first (tap the checkbox on each).");
            return;
        }
        const input = window.prompt(
            `Move ${selectedStopIds.size} stop(s) to which position? (1 = first)`,
        );
        const targetPos = parseInt(input, 10);
        if (isNaN(targetPos) || targetPos < 1) return;
        const stops = [...(selectedRoute.stops || [])];
        const moving = stops.filter((s) => selectedStopIds.has(s.id));
        const remaining = stops.filter((s) => !selectedStopIds.has(s.id));
        const target = Math.min(Math.max(0, targetPos - 1), remaining.length);
        const reordered = [
            ...remaining.slice(0, target),
            ...moving,
            ...remaining.slice(target),
        ];
        setSelectedRoute({ ...selectedRoute, stops: reordered });
        clearSelection();
        await persistOrder(reordered);
        await refreshSelectedRoute();
        toast.success(`Moved ${moving.length} stop(s) to position ${targetPos}`);
    };

    const togglePin = async (stop) => {
        const nextPinned = !stop.pinned;
        try {
            await api.post(`/routes/${selectedRoute.id}/stops/${stop.id}/pin`,
                { pinned: nextPinned });
            toast.success(nextPinned ? "Stop pinned in place" : "Pin removed");
            await refreshSelectedRoute();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Pin failed");
        }
    };

    const reverseStreet = async (stop) => {
        try {
            const { data } = await api.post(`/routes/${selectedRoute.id}/reverse-street`,
                { anchor_stop_id: stop.id });
            if (data.changed) {
                toast.success(`Reversed ${data.reversed_count} stops on "${data.street}"`);
                await load();
            } else {
                toast.info("Nothing to reverse — the street only has one stop in this run.");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reverse failed");
        }
    };

    const loadTemplates = async () => {
        if (!activeDay) return;
        try {
            const { data } = await api.get(`/route-templates?day=${activeDay}`);
            setTemplates(data);
        } catch { /* ignore */ }
    };
    useEffect(() => { loadTemplates(); /* eslint-disable-next-line */ }, [activeDay]);

    const saveAsMaster = async () => {
        if (!selectedRoute?.stops?.length) return;
        const name = window.prompt(
            `Save "${selectedRoute.name}" as a master template?\n\n` +
            `Every ${titleCase(activeDay)} run you can hit "Use Master" to instantly ` +
            `restore this exact order.\n\nName for this template:`,
            `${titleCase(activeDay)} Master`,
        );
        if (!name) return;
        try {
            const { data } = await api.post("/route-templates", {
                route_id: selectedRoute.id,
                name,
                day: activeDay,
            });
            toast.success(`Saved "${data.name}" — ${data.stop_count} stops`);
            await loadTemplates();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
    };

    const applyMasterTemplate = async (tpl) => {
        if (!selectedRoute) return;
        if (!window.confirm(
            `Apply "${tpl.name}" to ${selectedRoute.name}?\n\n` +
            `Every customer that matches keeps its position from the template.\n` +
            `New customers get inserted at their nearest neighbor.\n` +
            `Customers no longer on this route are dropped from the template.\n\nContinue?`
        )) return;
        try {
            const { data } = await api.post(`/routes/${selectedRoute.id}/apply-template`,
                { template_id: tpl.id });
            toast.success(
                `Applied "${data.template_name}" — ${data.matched} matched · ` +
                `${data.inserted} new inserted · ${data.dropped} template gaps`
            );
            setTemplatePickerOpen(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Apply failed");
        }
    };

    const openSequence = async () => {
        if (!selectedRoute) return;
        try {
            const { data } = await api.get(`/routes/${selectedRoute.id}/sequence`);
            setSeqData(data);
            setSeqOpen(true);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not load sequence");
        }
    };

    const optimizeRoute = async () => {
        if (!selectedRoute) return;
        setOptimizing(true);
        try {
            const { data } = await api.post(`/routes/${selectedRoute.id}/optimize`, {});
            if (data.strategy === "noop") {
                toast.info(`Skipped — ${data.reason || "nothing to optimize"}`);
            } else {
                const km = data.distance_meters ? (data.distance_meters / 1000).toFixed(1) : null;
                const min = data.duration_seconds ? Math.round(data.duration_seconds / 60) : null;
                const meta = km && min ? ` — ${km} km · ${min} min` : "";
                toast.success(`Optimized via ${data.strategy.replace(/_/g, " ")}${meta}`);
                await load();
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Optimization failed");
        } finally { setOptimizing(false); }
    };

    const balanceDay = async () => {
        if (!dayRoutes.length) return;
        if (dayRoutes.length < 2) return toast.info("Need at least 2 routes on this day to balance");
        if (!window.confirm(`Redistribute all ${activeDay} stops evenly across ${dayRoutes.length} routes?`)) return;
        setOptimizing(true);
        try {
            const { data } = await api.post("/routing/balance",
                { route_ids: dayRoutes.map((r) => r.id) });
            toast.success(`Balanced — ${data.routes.map((r) => `${r.stop_count}`).join(" / ")} stops`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Balance failed");
        } finally { setOptimizing(false); }
    };

    const resetSmsForRoute = async () => {
        if (!selectedRoute) return;
        if (!window.confirm(`Re-arm the "truck is 10 min away" SMS for every stop on ${selectedRoute.name}?`)) return;
        try {
            const { data } = await api.post(`/routes/${selectedRoute.id}/reset-eta-sms`);
            toast.success(`Re-armed ${data.reset_count} stops`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reset failed");
        }
    };

    const [generating, setGenerating] = useState(false);
    const [addNewOpen, setAddNewOpen] = useState(false);
    const [newCustomerPreview, setNewCustomerPreview] = useState(null);
    const [addingNew, setAddingNew] = useState(false);
    const [selectedTruckRoute, setSelectedTruckRoute] = useState("");

    const generateDayRoutes = async (opts = {}) => {
        const force = !!opts.force;
        if (!force) {
            const msg = `Auto-generate ${titleCase(activeDay)} routes from every active customer with route_day=${activeDay}? ` +
                        `If any ${titleCase(activeDay)} route already has stops, we'll STOP and ask you to use "Add new customers" instead.`;
            if (!window.confirm(msg)) return;
        }
        setGenerating(true);
        try {
            const { data } = await api.post("/routing/generate-day-routes",
                { day: activeDay, optimize: true, force });
            if (data.status === "empty") {
                toast.info(data.reason || "No customers assigned to this day");
            } else {
                const total = (data.created_routes || []).reduce((n, r) => n + r.stop_count, 0);
                toast.success(`Generated ${data.created_routes.length} route${data.created_routes.length === 1 ? "" : "s"} with ${total} stops`);
            }
            await load();
        } catch (e) {
            const detail = e.response?.data?.detail;
            const status = e.response?.data?.status;
            if (status === "existing_routes") {
                // Loud warn — protects last week's split.
                const routes = e.response.data.existing_routes || [];
                const summary = routes.map(r => `• ${r.name} (${r.stop_count} stops)`).join("\n");
                const confirmed = window.confirm(
                    `⚠️  ${titleCase(activeDay)} routes already have stops:\n\n${summary}\n\n` +
                    `Regenerating will REPLACE these routes and lose any manual splits or ordering.\n\n` +
                    `RECOMMENDED: Cancel and click "Add new customers" instead — it appends only newly-imported customers without touching existing stops.\n\n` +
                    `Regenerate anyway (destructive)?`,
                );
                if (confirmed) {
                    await generateDayRoutes({ force: true });
                }
                return;
            }
            toast.error(detail || "Generate failed");
        } finally { setGenerating(false); }
    };

    const openAddNewCustomers = async () => {
        setNewCustomerPreview(null);
        setSelectedTruckRoute("");
        setAddNewOpen(true);
        try {
            const { data } = await api.get("/routing/new-customers-preview",
                { params: { day: activeDay } });
            setNewCustomerPreview(data);
            // Pre-select the first truck option so the dialog is usable in one click.
            if (data.truck_options?.length) {
                setSelectedTruckRoute(data.truck_options[0].route_id);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't load new customers");
            setAddNewOpen(false);
        }
    };

    const commitAddNewCustomers = async () => {
        if (!selectedTruckRoute) {
            toast.error("Pick a truck first");
            return;
        }
        setAddingNew(true);
        try {
            const { data } = await api.post("/routing/add-new-customers", {
                day: activeDay,
                target_route_id: selectedTruckRoute,
            });
            if (data.added === 0) {
                toast.info("No new customers to add");
            } else {
                toast.success(`Added ${data.added} new customer${data.added === 1 ? "" : "s"} to ${data.route_name}`);
            }
            setAddNewOpen(false);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Add failed");
        } finally { setAddingNew(false); }
    };

    const [resetting, setResetting] = useState(false);
    const resetDayForNextWeek = async () => {
        const msg = `Reset ALL ${titleCase(activeDay)} routes for next week? ` +
                    `Every completed / skipped stop goes back to "pending". ` +
                    `Stop order, pins, and customer assignments are preserved.\n\n` +
                    `Stops for customers who moved to a different day, ` +
                    `churned, or were deleted will also be pruned.`;
        if (!window.confirm(msg)) return;
        setResetting(true);
        try {
            const { data } = await api.post("/routing/reset-day-for-next-week",
                { day: activeDay });
            if (data.status === "empty") {
                toast.info(data.reason || "No routes to reset");
            } else {
                const orph = data.orphans_removed
                    ? ` · pruned ${data.orphans_removed} orphaned stop${data.orphans_removed === 1 ? "" : "s"}`
                    : "";
                toast.success(`Reset ${data.stops_reset_from_completed} stops across ${data.routes_reset} route${data.routes_reset === 1 ? "" : "s"}${orph}`);
            }
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reset failed");
        } finally { setResetting(false); }
    };

    const [pruning, setPruning] = useState(false);
    const pruneOrphans = async () => {
        if (!selectedRoute) return;
        setPruning(true);
        try {
            const { data } = await api.post(`/routes/${selectedRoute.id}/prune-orphans`);
            if (data.status === "clean") {
                toast.info("No orphaned stops — count is already accurate");
            } else {
                const reasons = (data.orphan_reasons || [])
                    .slice(0, 5)
                    .map((r) => `${r.customer_name || "?"}: ${r.reason}`).join("\n");
                toast.success(
                    `Removed ${data.orphans_removed} orphaned stop${data.orphans_removed === 1 ? "" : "s"}. New total: ${data.total_stops}` +
                    (reasons ? `\n\n${reasons}` : ""),
                    { duration: 8000 },
                );
                await load();
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Prune failed");
        } finally { setPruning(false); }
    };

    return (
        <div data-testid="routing-page">
            <PageHeader
                title="Residential Routing"
                subtitle="Drag to reorder · Pin stops in place · Save the perfect order as a Master template and reuse next week."
                actions={
                    <>
                        <Button variant="outline" size="sm" onClick={generateDayRoutes}
                                disabled={generating}
                                data-testid="routing-generate-day-btn"
                                title={`Auto-build ${titleCase(activeDay)} routes from every active customer whose route_day matches`}>
                            <Zap className="w-4 h-4 mr-1" />
                            {generating ? "Generating…" : `Generate ${titleCase(activeDay)}`}
                        </Button>
                        <Button variant="outline" size="sm" onClick={openAddNewCustomers}
                                data-testid="routing-add-new-customers-btn"
                                title={`Append only newly-imported ${titleCase(activeDay)} customers to a specific truck — never touches existing stops`}>
                            <UserPlus className="w-4 h-4 mr-1" />
                            Add new customers
                        </Button>
                        <Button variant="outline" size="sm" onClick={resetDayForNextWeek}
                                disabled={resetting}
                                data-testid="routing-reset-day-btn"
                                title={`Reset every stop on ${titleCase(activeDay)}'s routes back to pending for next week's pickup`}>
                            <RotateCcw className="w-4 h-4 mr-1" />
                            {resetting ? "Resetting…" : "Reset for next week"}
                        </Button>
                        <Button variant="outline" size="sm" onClick={resetSmsForRoute}
                                disabled={!selectedRoute?.stops?.length}
                                data-testid="routing-reset-sms-btn"
                                title="Re-arm the 10-min-away SMS for all stops on this route">
                            <MessageSquare className="w-4 h-4 mr-1" />Reset SMS
                        </Button>
                        <Button variant="outline" size="sm" onClick={balanceDay}
                                disabled={optimizing || dayRoutes.length < 2}
                                data-testid="routing-balance-btn">
                            <Scale className="w-4 h-4 mr-1" />Balance {titleCase(activeDay)}
                        </Button>
                        <Button variant="outline" size="sm" onClick={openSequence}
                                disabled={!selectedRoute?.stops?.length}
                                data-testid="routing-view-sequence-btn">
                            <ListOrdered className="w-4 h-4 mr-1" />View sequence
                        </Button>
                        <div className="flex gap-1">
                            <Button size="sm" onClick={() => globalOptimize(false)}
                                    disabled={optimizing || !selectedRoute?.stops?.length}
                                    data-testid="routing-global-optimize-btn"
                                    title="Uber-style TSP with estimated drive times — best for weekly re-runs">
                                <Sparkles className="w-4 h-4 mr-1" />
                                {optimizing ? "Solving…" : "Global optimize"}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => globalOptimize(true)}
                                    disabled={optimizing || !selectedRoute?.stops?.length}
                                    data-testid="routing-global-optimize-hd-btn"
                                    title="High accuracy: refines with real Google drive times (~$0.25 per run)">
                                HD
                            </Button>
                        </div>
                        <Button size="sm" variant="outline" onClick={optimizeRoute}
                                disabled={optimizing || !selectedRoute?.stops?.length}
                                data-testid="routing-optimize-btn">
                            <Wand2 className="w-4 h-4 mr-1" />
                            {optimizing ? "Optimizing…" : "Quick optimize"}
                        </Button>
                    </>
                }
            />
            <div className="px-8 py-6 space-y-4">
                {/* Day tabs */}
                <div className="flex gap-1 surface border border-border rounded-md p-1 w-fit" data-testid="day-tabs">
                    {DAYS.map((d) => (
                        <button key={d}
                                onClick={() => { setActiveDay(d); const r = routes.find((x) => x.day === d); if (r) setSelectedRoute(r); }}
                                data-testid={`day-tab-${d}`}
                                className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded transition-colors ${
                                    activeDay === d ? "bg-foreground text-background font-semibold" : "text-muted-foreground hover:text-foreground"
                                }`}>
                            {d.slice(0, 3)}
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Route picker */}
                    <Card className="lg:col-span-1">
                        <CardContent className="p-4">
                            <div className="overline mb-3 px-2">Routes — {titleCase(activeDay)}</div>
                            {dayRoutes.length === 0 && <div className="text-sm text-muted-foreground p-3">No routes for this day.</div>}
                            <div className="space-y-1">
                                {dayRoutes.map((r) => {
                                    const total = r.stops?.length || 0;
                                    const done = (r.stops || []).filter(
                                        (s) => s.status === "completed").length;
                                    const pct = total ? Math.round((done / total) * 100) : 0;
                                    return (
                                        <button key={r.id}
                                                onClick={async () => {
                                                    // Eagerly refetch the fresh version so the detail
                                                    // panel doesn't flash a stale copy while the WS
                                                    // handshake completes.
                                                    setSelectedRoute(r);
                                                    try {
                                                        const { data } = await api.get(`/routes/${r.id}`);
                                                        setSelectedRoute(data);
                                                        setRoutes((rs) => rs.map((rr) =>
                                                            rr.id === r.id ? data : rr));
                                                    } catch { /* silent */ }
                                                }}
                                                data-testid={`route-pick-${r.id}`}
                                                className={`w-full text-left px-3 py-2.5 rounded-md border transition-colors ${
                                                    selectedRoute?.id === r.id ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"
                                                }`}>
                                            <div className="flex items-center justify-between">
                                                <div className="font-medium text-sm">{r.name}</div>
                                                <StatusPill status={r.status} />
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3">
                                                <span><Truck className="w-3 h-3 inline mr-1" />{titleCase(r.truck_type)}</span>
                                                <span>{r.stops?.length || 0} stops</span>
                                                {r.zone && <span>{r.zone}</span>}
                                            </div>
                                            {total > 0 && (
                                                <div className="mt-2 flex items-center gap-2" data-testid={`route-pick-progress-${r.id}`}>
                                                    <div className="flex-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                                                        <div className={`h-full transition-all ${pct === 100 ? "bg-emerald-500" : "bg-primary"}`}
                                                             style={{ width: `${pct}%` }} />
                                                    </div>
                                                    <span className="text-[10px] font-mono tabular text-muted-foreground shrink-0">
                                                        {done}/{total} · {pct}%
                                                    </span>
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Stops list with drag */}
                    <Card className="lg:col-span-2">
                        <CardContent className="p-0">
                            {selectedRoute ? (
                                <>
                                    <div className="p-5 border-b border-border flex items-center justify-between gap-3 flex-wrap">
                                        <div>
                                            <div className="overline mb-1">Stops · {selectedRoute.zone || "—"}</div>
                                            <h3 className="font-heading text-lg font-semibold">{selectedRoute.name}</h3>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <RouteProgressBadge route={selectedRoute} />
                                            <Button size="sm" variant="outline"
                                                    onClick={openDedupeDialog}
                                                    disabled={!selectedRoute?.stops?.length}
                                                    data-testid="remove-duplicates-btn">
                                                <Copy className="w-4 h-4 mr-1" />Remove duplicates
                                            </Button>
                                            <Button size="sm" variant="outline"
                                                    onClick={async () => {
                                                        if (!window.confirm(
                                                            `Clear all ${selectedRoute.stops?.length || 0} stops from "${selectedRoute.name}"?\n\nUse this to wipe and re-generate from scratch. This doesn't delete customers — just their spot on this route.`
                                                        )) return;
                                                        try {
                                                            await api.post(`/routes/${selectedRoute.id}/clear`);
                                                            toast.success("Route cleared — click Generate to rebuild");
                                                            await load();
                                                        } catch (e) {
                                                            toast.error(e.response?.data?.detail || "Clear failed");
                                                        }
                                                    }}
                                                    disabled={!selectedRoute?.stops?.length}
                                                    data-testid="clear-route-btn"
                                                    className="text-red-600 hover:text-red-700">
                                                <X className="w-4 h-4 mr-1" />Clear route
                                            </Button>
                                            <Button size="sm" variant="outline"
                                                    onClick={pruneOrphans}
                                                    disabled={pruning || !selectedRoute?.stops?.length}
                                                    data-testid="prune-orphans-btn"
                                                    title="Remove stops for customers who moved to a different day, churned, or were deleted"
                                                    className="text-amber-700 hover:text-amber-800 border-amber-500/40">
                                                <Sparkles className="w-4 h-4 mr-1" />
                                                {pruning ? "Pruning…" : "Prune orphans"}
                                            </Button>
                                            <Button size="sm" variant="outline"
                                                    onClick={saveAsMaster}
                                                    disabled={!selectedRoute?.stops?.length}
                                                    data-testid="save-master-btn"
                                                    title="Snapshot this exact stop order as a reusable template">
                                                <Save className="w-4 h-4 mr-1" />Save as Master
                                            </Button>
                                            <Button size="sm" variant="outline"
                                                    onClick={() => setTemplatePickerOpen((v) => !v)}
                                                    disabled={templates.length === 0}
                                                    data-testid="use-master-btn"
                                                    title="Apply a saved master template to this route">
                                                <BookMarked className="w-4 h-4 mr-1" />
                                                Use Master {templates.length > 0 && `(${templates.length})`}
                                            </Button>
                                            <Button size="sm"
                                                    onClick={publishRoute}
                                                    disabled={publishing || !selectedRoute?.stops?.length}
                                                    data-testid="publish-route-btn"
                                                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                                    title="Push this route to all driver phones — the app auto-refreshes">
                                                <Radio className="w-4 h-4 mr-1" />
                                                {publishing ? "Publishing…" : "Publish"}
                                            </Button>
                                        </div>
                                        {selectedRoute?.published_at && (
                                            <div className="text-[10px] text-emerald-600 dark:text-emerald-400 basis-full text-right mt-1">
                                                Last published {new Date(selectedRoute.published_at).toLocaleString()}
                                                {selectedRoute.published_by && ` by ${selectedRoute.published_by}`}
                                            </div>
                                        )}
                                    </div>

                                    {/* Master template picker */}
                                    {templatePickerOpen && templates.length > 0 && (
                                        <div className="px-5 py-3 border-b border-border bg-secondary/30 space-y-2"
                                             data-testid="template-picker">
                                            <div className="text-xs text-muted-foreground">
                                                Pick a saved master route for {titleCase(activeDay)}:
                                            </div>
                                            {templates.map((tpl) => (
                                                <div key={tpl.id} className="flex items-center justify-between p-2 rounded border border-border bg-background"
                                                     data-testid={`template-${tpl.id}`}>
                                                    <div>
                                                        <div className="text-sm font-medium">{tpl.name}</div>
                                                        <div className="text-[11px] text-muted-foreground">
                                                            {tpl.stop_count} stops · saved by {tpl.saved_by} · {new Date(tpl.saved_at).toLocaleDateString()}
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <Button size="sm" onClick={() => applyMasterTemplate(tpl)}
                                                                data-testid={`apply-template-${tpl.id}`}>
                                                            Apply
                                                        </Button>
                                                        <Button size="sm" variant="ghost"
                                                                onClick={async () => {
                                                                    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
                                                                    await api.delete(`/route-templates/${tpl.id}`);
                                                                    await loadTemplates();
                                                                    toast.success("Deleted");
                                                                }}
                                                                data-testid={`delete-template-${tpl.id}`}>
                                                            <X className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Search / filter stops */}
                                    <div className="px-5 py-3 border-b border-border flex items-center gap-2">
                                        <div className="relative flex-1">
                                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <Input
                                                value={stopSearch}
                                                onChange={(e) => setStopSearch(e.target.value)}
                                                placeholder="Search name or address in this route…"
                                                className="pl-9 h-9"
                                                data-testid="stop-search-input"
                                            />
                                        </div>
                                        {stopSearchLower && (
                                            <>
                                                <span className="text-xs text-muted-foreground whitespace-nowrap"
                                                      data-testid="stop-search-count">
                                                    {searchHitCount} match{searchHitCount === 1 ? "" : "es"}
                                                </span>
                                                <Button size="sm" variant="ghost"
                                                        onClick={() => setStopSearch("")}
                                                        data-testid="stop-search-clear">
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            </>
                                        )}
                                    </div>

                                    {/* Multi-select action bar */}
                                    {selectedStopIds.size > 0 && (
                                        <div className="px-5 py-3 border-b border-border bg-primary/5 flex items-center gap-2 flex-wrap"
                                             data-testid="multiselect-actions">
                                            <span className="text-sm font-medium">{selectedStopIds.size} selected</span>
                                            <Button size="sm" variant="outline" onClick={moveSelectedToPosition}
                                                    data-testid="move-to-position-btn">
                                                <MoveVertical className="w-4 h-4 mr-1" />Move to position…
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={openReassignDialog}
                                                    data-testid="reassign-truck-btn">
                                                <Truck className="w-4 h-4 mr-1" />Move to truck…
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={clearSelection}
                                                    data-testid="clear-selection-btn">
                                                Clear
                                            </Button>
                                        </div>
                                    )}
                                    <div className="max-h-[60vh] overflow-y-auto divide-y divide-border" data-testid="route-stops-list">
                                        {selectedRoute.stops?.length === 0 && (
                                            <div className="p-8 text-center text-sm text-muted-foreground">No stops on this route yet.</div>
                                        )}
                                        {selectedRoute.stops?.length > 0 && stopSearchLower && searchHitCount === 0 && (
                                            <div className="p-8 text-center text-sm text-muted-foreground"
                                                 data-testid="stop-search-empty">
                                                No stops match "{stopSearch}".
                                            </div>
                                        )}
                                        {selectedRoute.stops?.map((s, idx) => {
                                            if (!stopMatches(s)) return null;
                                            const allIds = (selectedRoute.stops || []).map((x) => x.id);
                                            const isSelected = selectedStopIds.has(s.id);
                                            return (
                                            <div key={s.id}
                                                 draggable
                                                 onDragStart={(e) => e.dataTransfer.setData("idx", idx)}
                                                 onDragOver={(e) => e.preventDefault()}
                                                 onDrop={(e) => { const from = parseInt(e.dataTransfer.getData("idx")); moveStop(from, idx); }}
                                                 data-testid={`route-stop-${s.id}`}
                                                 className={`flex items-center gap-3 px-5 py-3 hover:bg-secondary/40 cursor-move ${isSelected ? "bg-primary/5" : ""} ${s.pinned ? "border-l-2 border-l-amber-500" : ""}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) => toggleStopSelect(s.id, e.nativeEvent.shiftKey, allIds)}
                                                    onDragStart={(e) => e.stopPropagation()}
                                                    data-testid={`select-stop-${s.id}`}
                                                    className="cursor-pointer" />
                                                <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                                                <div className="font-mono text-xs text-muted-foreground w-8 text-right">#{idx + 1}</div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium truncate flex items-center gap-1.5">
                                                        {s.pinned && <Pin className="w-3 h-3 text-amber-500 shrink-0" />}
                                                        {s.customer_name}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                                                        <MapPin className="w-3 h-3" /> {s.address}
                                                    </div>
                                                </div>
                                                {s.sms_10min_sent && (
                                                    <span data-testid={`stop-sms-badge-${s.id}`}
                                                          title={`SMS sent ${s.sms_10min_sent_at ? new Date(s.sms_10min_sent_at).toLocaleTimeString() : ""}`}
                                                          className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200/60 dark:border-emerald-900 px-1.5 py-0.5 rounded flex items-center gap-1">
                                                        <MessageSquare className="w-3 h-3" />SMS
                                                    </span>
                                                )}
                                                {s.completed_at && (
                                                    <div className="text-[10px] text-emerald-700 dark:text-emerald-400 tabular whitespace-nowrap"
                                                         title={`Completed ${new Date(s.completed_at).toLocaleString()}`}
                                                         data-testid={`stop-completed-at-${s.id}`}>
                                                        {new Date(s.completed_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                                                    </div>
                                                )}
                                                <StatusPill status={s.status} />
                                                {s.photo_url && (
                                                    <Button size="sm" variant="ghost"
                                                            onClick={() => setPhotoLightbox({ url: s.photo_url, stop: s })}
                                                            title="View driver's photo"
                                                            data-testid={`stop-photo-${s.id}`}
                                                            className="text-blue-600 dark:text-blue-400">
                                                        <Camera className="w-4 h-4" />
                                                    </Button>
                                                )}
                                                <Button size="sm" variant="ghost"
                                                        onClick={() => togglePin(s)}
                                                        title={s.pinned ? "Unpin — allow optimizer to move this stop" : "Pin — lock this stop in place across future optimizations"}
                                                        data-testid={`pin-stop-${s.id}`}>
                                                    {s.pinned
                                                        ? <PinOff className="w-4 h-4 text-amber-500" />
                                                        : <Pin className="w-4 h-4 opacity-40" />}
                                                </Button>
                                                <Button size="sm" variant="ghost"
                                                        onClick={() => reverseStreet(s)}
                                                        title="Reverse the direction driven on this street"
                                                        data-testid={`reverse-street-${s.id}`}>
                                                    <ArrowUpDown className="w-4 h-4 opacity-60" />
                                                </Button>
                                                {s.status !== "completed" && (
                                                    <Button size="sm" variant="ghost"
                                                            onClick={() => markStopComplete(s.id)}
                                                            data-testid={`complete-stop-${s.id}`}>
                                                        <Check className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                            );
                                        })}
                                    </div>
                                </>
                            ) : (
                                <div className="p-12 text-center text-sm text-muted-foreground">Select a route to view stops.</div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
            <SequenceDialog open={seqOpen} setOpen={setSeqOpen} data={seqData} />

            {/* Remove-duplicates confirmation dialog */}
            <Dialog open={dedupeOpen} onOpenChange={setDedupeOpen}>
                <DialogContent className="max-w-2xl" data-testid="dedupe-dialog">
                    <DialogHeader>
                        <DialogTitle>Remove duplicate stops</DialogTitle>
                        <DialogDescription className="text-xs">
                            We keep the best one from each group (completed + with-photo &gt; completed &gt; earliest position).
                            Un-check any duplicate you want to <b>keep</b> before confirming.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="max-h-[60vh] overflow-auto space-y-3 py-2">
                        {dedupeBusy && !dedupeData && (
                            <div className="text-sm text-muted-foreground py-8 text-center">Scanning…</div>
                        )}
                        {dedupeData && dedupeData.group_count === 0 && (
                            <div className="text-sm text-emerald-700 dark:text-emerald-400 py-8 text-center"
                                 data-testid="dedupe-none">
                                No duplicates found — this route is clean.
                            </div>
                        )}
                        {dedupeData?.groups?.map((g) => (
                            <div key={g.key} className="border border-border rounded-md p-3 bg-secondary/30"
                                 data-testid={`dedupe-group-${g.key}`}>
                                <div className="text-sm font-semibold">
                                    {g.customer_name || "(no customer)"}
                                </div>
                                <div className="text-[11px] text-muted-foreground">{g.address}</div>
                                <div className="mt-2 space-y-1">
                                    <div className="flex items-center gap-2 text-xs">
                                        <Check className="w-3 h-3 text-emerald-600" />
                                        <span className="text-emerald-700 dark:text-emerald-400 font-medium">
                                            Keep — position {g.keeper_position}
                                        </span>
                                    </div>
                                    {g.duplicates.map((d) => (
                                        <label key={d.stop_id}
                                               className="flex items-center gap-2 text-xs cursor-pointer hover:bg-secondary/60 rounded px-1 py-0.5"
                                               data-testid={`dedupe-dup-${d.stop_id}`}>
                                            <input type="checkbox"
                                                   checked={dedupeSelected.has(d.stop_id)}
                                                   onChange={() => toggleDedupeStop(d.stop_id)}
                                                   className="rounded" />
                                            <span className={dedupeSelected.has(d.stop_id) ? "text-red-600 line-through" : "text-muted-foreground"}>
                                                Remove — position {d.position} · {d.status}
                                                {d.has_photo && " · has photo"}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setDedupeOpen(false)}>Cancel</Button>
                        <Button variant="destructive"
                                onClick={confirmDedupe}
                                disabled={dedupeBusy || dedupeSelected.size === 0}
                                data-testid="dedupe-confirm-btn">
                            {dedupeBusy ? "Removing…" : `Remove ${dedupeSelected.size} duplicate${dedupeSelected.size === 1 ? "" : "s"}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reassign-selected-stops-to-another-truck dialog */}
            <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
                <DialogContent data-testid="reassign-dialog">
                    <DialogHeader>
                        <DialogTitle>Move {selectedStopIds.size} stop{selectedStopIds.size === 1 ? "" : "s"} to another truck</DialogTitle>
                        <DialogDescription className="text-xs">
                            Only shows routes running <b>{selectedRoute?.day ? titleCase(selectedRoute.day) : "the same day"}</b>.
                            Stops keep their status, notes, photos, and coordinates — they just show up on the other truck's app.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        {!creatingRoute && (
                            <>
                                <label className="text-xs uppercase tracking-wider text-muted-foreground">Destination route</label>
                                <Select value={reassignTargetId} onValueChange={setReassignTargetId}>
                                    <SelectTrigger data-testid="reassign-target-select">
                                        <SelectValue placeholder={sameDayRoutes.length ? "Choose a route…" : "No other route on this day yet"} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {sameDayRoutes.map((r) => (
                                            <SelectItem key={r.id} value={r.id} data-testid={`reassign-target-${r.id}`}>
                                                {r.name}
                                                {r.truck_type ? ` · ${titleCase(r.truck_type)}` : ""}
                                                {" · "}{(r.stops || []).length} stop{(r.stops || []).length === 1 ? "" : "s"}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <div className="text-xs pt-1">
                                    <button type="button"
                                            className="text-primary hover:underline"
                                            onClick={() => setCreatingRoute(true)}
                                            data-testid="reassign-create-new-toggle">
                                        + Create a new {selectedRoute?.day ? titleCase(selectedRoute.day) : ""} route on the fly
                                    </button>
                                </div>
                            </>
                        )}

                        {creatingRoute && (
                            <div className="space-y-3 border border-dashed border-primary/30 rounded-md p-3 bg-primary/5">
                                <div className="text-xs font-semibold text-primary uppercase tracking-wider">
                                    New {selectedRoute?.day ? titleCase(selectedRoute.day) : ""} route
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Route name</label>
                                    <Input
                                        placeholder={`e.g. ${selectedRoute?.day ? titleCase(selectedRoute.day) : "Monday"} Truck B`}
                                        value={newRouteName}
                                        onChange={(e) => setNewRouteName(e.target.value)}
                                        data-testid="reassign-new-name" />
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Assign to truck</label>
                                    <Select value={newRouteTruckId} onValueChange={setNewRouteTruckId}>
                                        <SelectTrigger data-testid="reassign-new-truck-select">
                                            <SelectValue placeholder={trucks.length ? "Pick a truck…" : "Loading trucks…"} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {trucks.map((t) => (
                                                <SelectItem key={t.id} value={t.id}
                                                            data-testid={`reassign-new-truck-${t.id}`}>
                                                    {t.nickname || t.make + " " + t.model}
                                                    {t.license_plate ? ` · ${t.license_plate}` : ""}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Body type</label>
                                    <Select value={newRouteTruckType} onValueChange={setNewRouteTruckType}>
                                        <SelectTrigger data-testid="reassign-new-body-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="sideload">Sideload</SelectItem>
                                            <SelectItem value="rearload">Rear load</SelectItem>
                                            <SelectItem value="frontload">Front load</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <button type="button"
                                        className="text-xs text-muted-foreground hover:underline"
                                        onClick={() => { setCreatingRoute(false); setNewRouteName(""); setNewRouteTruckId(""); }}
                                        data-testid="reassign-cancel-create">
                                    ← Pick an existing route instead
                                </button>
                            </div>
                        )}

                        {!creatingRoute && sameDayRoutes.length === 0 && (
                            <p className="text-xs text-muted-foreground">
                                No other route exists for {selectedRoute?.day ? titleCase(selectedRoute.day) : "this day"}.
                                Tap <b>+ Create a new route</b> above to spin one up right now.
                            </p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setReassignOpen(false)}>Cancel</Button>
                        <Button onClick={confirmReassign}
                                disabled={reassignBusy
                                    || (!creatingRoute && !reassignTargetId)
                                    || (creatingRoute && (!newRouteName.trim() || !newRouteTruckId))}
                                data-testid="reassign-confirm-btn">
                            {reassignBusy ? "Moving…" : `Move ${selectedStopIds.size} stop${selectedStopIds.size === 1 ? "" : "s"}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Driver photo lightbox */}
            {photoLightbox && (
                <Dialog open={!!photoLightbox} onOpenChange={(o) => !o && setPhotoLightbox(null)}>
                    <DialogContent className="max-w-3xl" data-testid="photo-lightbox">
                        <DialogHeader>
                            <DialogTitle className="font-heading">
                                {photoLightbox.stop.customer_name}
                            </DialogTitle>
                            <DialogDescription>
                                {photoLightbox.stop.address}
                                {photoLightbox.stop.completed_at && (
                                    <> · Completed {new Date(photoLightbox.stop.completed_at).toLocaleString()}</>
                                )}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="flex flex-col items-center gap-3">
                            <img
                                src={photoLightbox.url.startsWith("http") ? photoLightbox.url : photoLightbox.url}
                                alt={`Driver photo at ${photoLightbox.stop.customer_name}`}
                                className="max-h-[70vh] max-w-full rounded-md border border-border"
                                data-testid="photo-lightbox-img"
                            />
                            <a href={photoLightbox.url} target="_blank" rel="noreferrer"
                               className="text-xs text-muted-foreground hover:underline">
                                Open in new tab
                            </a>
                        </div>
                    </DialogContent>
                </Dialog>
            )}

            {/* Add-new-customers dialog: appends only newly-imported customers
                for the active day to the truck the office picks. Never
                touches existing stops. */}
            <Dialog open={addNewOpen} onOpenChange={setAddNewOpen}>
                <DialogContent className="sm:max-w-lg" data-testid="add-new-customers-dialog">
                    <DialogHeader>
                        <DialogTitle>Add new customers · {titleCase(activeDay)}</DialogTitle>
                        <DialogDescription>
                            Only customers that aren't on any {titleCase(activeDay)} route yet.
                            Existing stops + split routes stay exactly as they are.
                        </DialogDescription>
                    </DialogHeader>

                    {!newCustomerPreview ? (
                        <div className="py-6 text-sm text-muted-foreground">Loading…</div>
                    ) : newCustomerPreview.unassigned_count === 0 ? (
                        <div className="py-6 text-sm text-muted-foreground text-center">
                            No unassigned {titleCase(activeDay)} customers — everyone is already on a route.
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="text-sm">
                                <b>{newCustomerPreview.unassigned_count}</b> new customer{newCustomerPreview.unassigned_count === 1 ? "" : "s"} ready to append:
                            </div>
                            <div className="border border-border rounded-md max-h-48 overflow-y-auto text-xs" data-testid="add-new-preview-list">
                                {newCustomerPreview.unassigned.map((c) => (
                                    <div key={c.id} className="px-3 py-1.5 border-b border-border last:border-b-0 flex items-center justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-medium truncate">{c.name}</div>
                                            <div className="text-muted-foreground truncate">{c.address_short || "—"}</div>
                                        </div>
                                        {c.type === "residential_dumpster" && (
                                            <span className="text-[10px] uppercase text-blue-700 dark:text-blue-400 font-semibold">dumpster</span>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div>
                                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                                    Assign to
                                </label>
                                {newCustomerPreview.truck_options?.length ? (
                                    <Select value={selectedTruckRoute} onValueChange={setSelectedTruckRoute}>
                                        <SelectTrigger className="mt-1.5" data-testid="add-new-truck-select">
                                            <SelectValue placeholder="Pick a truck" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {newCustomerPreview.truck_options.map((o) => (
                                                <SelectItem key={o.route_id} value={o.route_id} data-testid={`add-new-truck-${o.truck_id}`}>
                                                    {o.truck_name || o.route_name} · currently {o.current_stop_count} stops
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <div className="text-xs text-amber-600 mt-1.5">
                                        No existing {titleCase(activeDay)} routes yet — click "Generate {titleCase(activeDay)}" first, then come back here to add new imports.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddNewOpen(false)}>Cancel</Button>
                        <Button
                            onClick={commitAddNewCustomers}
                            disabled={
                                addingNew ||
                                !selectedTruckRoute ||
                                !newCustomerPreview ||
                                newCustomerPreview.unassigned_count === 0
                            }
                            data-testid="add-new-commit-btn"
                        >
                            {addingNew ? "Adding…" :
                                newCustomerPreview
                                    ? `Add ${newCustomerPreview.unassigned_count} customer${newCustomerPreview.unassigned_count === 1 ? "" : "s"}`
                                    : "Add"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


function RouteProgressBadge({ route }) {
    const stops = route?.stops || [];
    const total = stops.length;
    const completed = stops.filter((s) => s.status === "completed").length;
    const cartNotOut = stops.filter((s) => s.status === "cart_not_out").length;
    const pct = total ? Math.round((completed / total) * 100) : 0;
    return (
        <div className="flex items-center gap-2" data-testid="route-progress-badge">
            <div className="w-32 h-2 bg-secondary rounded overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all"
                     style={{ width: `${pct}%` }} />
            </div>
            <div className="text-right leading-tight">
                <div className="font-mono text-sm tabular font-semibold" data-testid="route-progress-pct">
                    {completed}/{total} · {pct}%
                </div>
                {cartNotOut > 0 && (
                    <div className="text-[10px] text-amber-600 dark:text-amber-400">
                        {cartNotOut} cart-not-out
                    </div>
                )}
            </div>
        </div>
    );
}


function SequenceDialog({ open, setOpen, data }) {    if (!data) return null;

    const totalMin = data.estimated_total_seconds
        ? Math.round(data.estimated_total_seconds / 60)
        : null;

    const exportCsv = () => {
        const header = "Seq,Customer,Address,Carts,Phone,Notes,Completed\n";
        const rows = data.stops.map((s) => [
            s.sequence,
            `"${(s.customer_name || "").replace(/"/g, '""')}"`,
            `"${(s.address || "").replace(/"/g, '""')}"`,
            `${s.cart_quantity}x ${s.cart_size}`,
            s.phone || "",
            `"${(s.notes || "").replace(/"/g, '""')}"`,
            s.completed_at ? new Date(s.completed_at).toLocaleString() : "",
        ].join(",")).join("\n");
        const blob = new Blob([header + rows], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `route-${data.day || "day"}-${data.route_id.slice(0, 8)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const print = () => window.print();

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="sequence-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading flex items-center gap-2">
                        <ListOrdered className="w-5 h-5" />
                        Optimized stop sequence — {titleCase(data.day || "")}
                    </DialogTitle>
                    <DialogDescription>
                        <div className="flex flex-wrap gap-4 text-xs mt-2">
                            <span><strong>{data.stop_count}</strong> stops</span>
                            {totalMin && <span><strong>{totalMin} min</strong> estimated drive time</span>}
                            <span className="text-muted-foreground">Depot: {data.depot_address}</span>
                            {data.optimize_strategy && (
                                <span className="text-muted-foreground">Strategy: {data.optimize_strategy.replace(/_/g, " ")}</span>
                            )}
                        </div>
                    </DialogDescription>
                </DialogHeader>

                <div className="flex gap-2 pb-2 print:hidden">
                    <Button size="sm" variant="outline" onClick={exportCsv} data-testid="seq-export-csv-btn">
                        <Download className="w-4 h-4 mr-1" />Export CSV
                    </Button>
                    <Button size="sm" variant="outline" onClick={print} data-testid="seq-print-btn">
                        <Printer className="w-4 h-4 mr-1" />Print
                    </Button>
                </div>

                <div className="overflow-auto flex-1 border border-border rounded" data-testid="seq-table-wrap">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-secondary/90 backdrop-blur">
                            <tr>
                                <th className="text-left px-3 py-2 overline w-12">#</th>
                                <th className="text-left px-3 py-2 overline">Customer</th>
                                <th className="text-left px-3 py-2 overline">Address</th>
                                <th className="text-left px-3 py-2 overline">Carts</th>
                                <th className="text-left px-3 py-2 overline">Phone</th>
                                <th className="text-left px-3 py-2 overline">Notes</th>
                                <th className="text-left px-3 py-2 overline">Completed</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="border-t border-border bg-secondary/40">
                                <td className="px-3 py-2 font-mono text-xs">DEPOT</td>
                                <td className="px-3 py-2 font-medium" colSpan="6">Start · {data.depot_address}</td>
                            </tr>
                            {data.stops.map((s) => (
                                <tr key={s.sequence} className="border-t border-border hover:bg-secondary/30"
                                    data-testid={`seq-row-${s.sequence}`}>
                                    <td className="px-3 py-2 font-mono text-xs">{s.sequence}</td>
                                    <td className="px-3 py-2 font-medium">{s.customer_name}</td>
                                    <td className="px-3 py-2 text-xs text-muted-foreground">{s.address}</td>
                                    <td className="px-3 py-2 text-xs">{s.cart_quantity}× {s.cart_size}</td>
                                    <td className="px-3 py-2 text-xs">{s.phone || "—"}</td>
                                    <td className="px-3 py-2 text-xs text-muted-foreground">{s.notes || ""}</td>
                                    <td className="px-3 py-2 text-xs text-emerald-700 dark:text-emerald-400 tabular">
                                        {s.completed_at ? new Date(s.completed_at).toLocaleString([], {
                                            month: "numeric", day: "numeric",
                                            hour: "numeric", minute: "2-digit"
                                        }) : ""}
                                    </td>
                                </tr>
                            ))}
                            <tr className="border-t border-border bg-secondary/40">
                                <td className="px-3 py-2 font-mono text-xs">RETURN</td>
                                <td className="px-3 py-2 font-medium" colSpan="6">End · {data.depot_address}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </DialogContent>
        </Dialog>
    );
}
